import React, { useState, useEffect, Component } from "react";
import {
  Badge,
  Card,
  Row,
  Col,
  Table,
  Spinner,
} from "react-bootstrap";
import Breadcrumb from "../../components/common/Breadcrumb";
import MatchScheduleCard from "./modules/MatchScheduleCard";
import stylesgfi from "./NationalSchoolGames.module.css";
import "./MatchFixturePage.css";

// ─── Error Boundary (catches render crashes and shows them instead of blank) ──
class TabErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(err) {
    return { error: err };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="alert alert-danger m-3">
          <strong>Render Error:</strong> {this.state.error.message}
          <pre className="mt-2 small" style={{ whiteSpace: "pre-wrap" }}>
            {this.state.error.stack?.split("\n").slice(0, 5).join("\n")}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// Backend base URL — matches the dashboard's ApiService configuration (localhost:5012 in dev)
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_BASE_URL = "https://apigmsbssa.shyenterprises.shop";

// Fixture ID for Bihar Women Kabaddi League 2026
const FIXTURE_ID = "69b13c1f08e6989a3a16d99a";
const USER_ID    = "691d82f3c286304f33dc9c11";
const TTL        = "4417";

// Primary: view route (validates userId + moniterd_ttl, no cookie needed)
const FIXTURE_API_URL = `${API_BASE_URL}/api/fixtures/view/${FIXTURE_ID}/${USER_ID}/${TTL}`;
// Fallback: fully open public route
const FIXTURE_PUBLIC_URL = `${API_BASE_URL}/api/fixtures/public/${FIXTURE_ID}`;

// Cache for team logos to avoid duplicate API calls
const teamLogoCache = {};

// ─── helper: Fetch team logo from backend API ──────────────────────────────
async function fetchTeamLogo(teamId) {
  if (!teamId) return null;
  
  // Return from cache if available
  if (teamLogoCache[teamId]) {
    return teamLogoCache[teamId];
  }

  try {
    // Try the GMS teams endpoint
    const response = await fetch(`${API_BASE_URL}/api/gms/teams/${teamId}/logo`);
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data?.teamLogo) {
        teamLogoCache[teamId] = data.data.teamLogo;
        console.log(`✓ Fetched logo for team ${teamId}:`, data.data.teamLogo);
        return data.data.teamLogo;
      }
    }
  } catch (error) {
    console.error(`✗ Failed to fetch logo for team ${teamId}:`, error);
  }
  return null;
}

// ─── Standings row team logo (async, with fallback shield icon) ──────────────
function StandingsTeamLogo({ teamId }) {
  const [logo, setLogo] = useState(null);
  useEffect(() => {
    if (!teamId) return;
    fetchTeamLogo(teamId).then(url => setLogo(url || null));
  }, [teamId]);
  return (
    <div style={{
      width: "40px", height: "40px", borderRadius: "50%", overflow: "hidden",
      background: "rgba(255,255,255,0.08)",
      border: "2px solid rgba(255,255,255,0.15)",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
      boxShadow: "0 2px 8px rgba(0,0,0,0.4)"
    }}>
      {logo
        ? <img src={logo} alt="team logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
        : <i className="bi bi-shield-fill" style={{ color: "rgba(255,255,255,0.25)", fontSize: "1.1rem" }} />
      }
    </div>
  );
}

// ─── helper: status → Bootstrap badge colour ────────────────────────────────
function statusBadgeColor(status) {
  const map = {
    pending: "secondary",
    scheduled: "info",
    live: "danger",
    completed: "success",
    bye: "warning",
  };
  return map[status] || "secondary";
}

// ─── helper: format a date + time string ────────────────────────────────────
function formatMatchDate(date, time) {
  if (!date) return "";
  const d = new Date(date);
  const dateStr = d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return time ? `${dateStr} ${time}` : dateStr;
}

// ─── Team Match Card (used for knockout / round-robin team formats) ──────────
function TeamMatchCard({ match }) {
  const status = match.status || "pending";
  const isCompleted = status === "completed";
  const isLive = status === "live";
  const isBye = match.isBye || status === "bye";

  return (
    <div
      className={`match-card ${isCompleted ? "completed" : ""} ${isLive ? "live" : ""} ${isBye ? "bye" : ""}`}
    >
      <div className="match-header">
        <span className="match-id">M-{match.matchNo}</span>
        <span className="match-date">
          {formatMatchDate(match.date || match.scheduledDate || match.scheduledAt, match.scheduledTime)}
        </span>
        <Badge bg={statusBadgeColor(status)} className="match-status">
          {status === "live" && <span className="live-dot"></span>}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      </div>
      <div className="match-teams">
        <div
          className={`team ${
            match.winner?.teamId === match.team1?.teamId && isCompleted
              ? "winner"
              : ""
          }`}
        >
          <span className="team-seed">
            {match.team1?.seed ? `${match.team1.seed}` : ""}
          </span>
          <span className="team-name">{match.team1?.name || "TBD"}</span>
          {match.winner?.teamId === match.team1?.teamId && isCompleted && (
            <i className="bi bi-check-circle-fill winner-icon"></i>
          )}
        </div>
        <div
          className={`team ${
            match.winner?.teamId === match.team2?.teamId && isCompleted
              ? "winner"
              : ""
          }`}
        >
          <span className="team-seed">
            {match.team2?.seed ? `${match.team2.seed}` : ""}
          </span>
          <span className="team-name">{match.team2?.name || "TBD"}</span>
          {match.winner?.teamId === match.team2?.teamId && isCompleted && (
            <i className="bi bi-check-circle-fill winner-icon"></i>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Individual Match Card ───────────────────────────────────────────────────
function IndividualMatchCard({ match }) {
  const status = match.status || "pending";
  const isCompleted = status === "completed";
  const isLive = status === "live";
  const isBye = match.isBye || status === "bye";

  return (
    <div
      className={`match-card ${isCompleted ? "completed" : ""} ${isLive ? "live" : ""} ${isBye ? "bye" : ""}`}
    >
      <div className="match-header">
        <span className="match-id">M-{match.matchNo}</span>
        <span className="match-date">
          {formatMatchDate(match.date || match.scheduledDate || match.scheduledAt, match.scheduledTime)}
        </span>
        <Badge bg={statusBadgeColor(status)} className="match-status">
          {status === "live" && <span className="live-dot"></span>}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      </div>
      <div className="match-teams">
        <div
          className={`team ${
            match.winner?.athleteId === match.athlete1?.athleteId && isCompleted
              ? "winner"
              : ""
          }`}
        >
          <span className="team-seed">
            {match.athlete1?.seed ? `${match.athlete1.seed}` : ""}
          </span>
          <div className="athlete-info">
            <span className="team-name">{match.athlete1?.name || "TBD"}</span>
            {match.athlete1?.district && (
              <span className="athlete-district">{match.athlete1.district}</span>
            )}
          </div>
          {match.winner?.athleteId === match.athlete1?.athleteId &&
            isCompleted && (
              <i className="bi bi-check-circle-fill winner-icon"></i>
            )}
        </div>
        <div
          className={`team ${
            match.winner?.athleteId === match.athlete2?.athleteId && isCompleted
              ? "winner"
              : ""
          }`}
        >
          <span className="team-seed">
            {match.athlete2?.seed ? `${match.athlete2.seed}` : ""}
          </span>
          <div className="athlete-info">
            <span className="team-name">{match.athlete2?.name || "TBD"}</span>
            {match.athlete2?.district && (
              <span className="athlete-district">{match.athlete2.district}</span>
            )}
          </div>
          {match.winner?.athleteId === match.athlete2?.athleteId &&
            isCompleted && (
              <i className="bi bi-check-circle-fill winner-icon"></i>
            )}
        </div>
      </div>
    </div>
  );
}

// ─── Knockout Match Card (pools-and-knockout) ────────────────────────────────
function KnockoutMatchCard({ match }) {
  const isCompleted = match.status === "completed";
  const isLive = match.status === "live";
  const isBye = match.isBye || match.status === "bye";
  const status = match.status || "pending";

  return (
    <div
      className={`match-card ${isCompleted ? "completed" : ""} ${isLive ? "live" : ""} ${isBye ? "bye" : ""}`}
    >
      <div className="match-header">
        <span className="match-id">M-{match.matchNo}</span>
        <Badge bg={statusBadgeColor(status)} className="match-status">
          {status === "live" && <span className="live-dot"></span>}
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
      </div>
      <div className="match-teams">
        <div
          className={`team ${
            match.winner?.teamId === match.team1?.teamId && isCompleted
              ? "winner"
              : ""
          }`}
        >
          <span className="team-name">{match.team1?.name || "TBD"}</span>
          {match.winner?.teamId === match.team1?.teamId && isCompleted && (
            <i className="bi bi-check-circle-fill winner-icon"></i>
          )}
        </div>
        <div
          className={`team ${
            match.winner?.teamId === match.team2?.teamId && isCompleted
              ? "winner"
              : ""
          }`}
        >
          <span className="team-name">{match.team2?.name || "TBD"}</span>
          {match.winner?.teamId === match.team2?.teamId && isCompleted && (
            <i className="bi bi-check-circle-fill winner-icon"></i>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tournament Bracket Tab ──────────────────────────────────────────────────
function BracketTab({ data }) {
  const rounds = data.rounds || [];
  const individualRounds = data.individualRounds || [];
  const bracket = data.bracket || [];
  const knockoutMatches = data.knockoutMatches || [];
  const knockoutRounds = data.knockoutRounds || [];

  const isIndividualSport =
    data.type === "individual" || individualRounds.length > 0;

  // Pick the right set of rounds to render
  let tournamentRounds = isIndividualSport ? individualRounds : rounds;
  let useKnockout = false;

  if (tournamentRounds.length === 0 && knockoutRounds.length > 0) {
    tournamentRounds = knockoutRounds;
    useKnockout = true;
  } else if (
    tournamentRounds.length === 0 &&
    knockoutRounds.length === 0 &&
    knockoutMatches.length > 0
  ) {
    // Reconstruct rounds from flat knockoutMatches
    const roundMap = new Map();
    for (const m of knockoutMatches) {
      const rn = m.roundNumber ?? 1;
      if (!roundMap.has(rn)) {
        roundMap.set(rn, {
          roundNumber: rn,
          roundName: m.round || `Round ${rn}`,
          matches: [],
        });
      }
      roundMap.get(rn).matches.push(m);
    }
    tournamentRounds = Array.from(roundMap.values()).sort(
      (a, b) => a.roundNumber - b.roundNumber
    );
    useKnockout = true;
  }

  // Handle legacy bracket format (flat array with pos/name/bye)
  if (tournamentRounds.length === 0 && bracket.length > 0) {
    const firstEntry = bracket[0];
    if (firstEntry.roundNumber !== undefined || firstEntry.matchNo !== undefined) {
      // bracket contains KnockoutMatch objects — reconstruct rounds
      const roundMap = new Map();
      for (const m of bracket) {
        const rn = m.roundNumber ?? 1;
        if (!roundMap.has(rn)) {
          roundMap.set(rn, {
            roundNumber: rn,
            roundName: m.round || `Round ${rn}`,
            matches: [],
          });
        }
        roundMap.get(rn).matches.push(m);
      }
      tournamentRounds = Array.from(roundMap.values()).sort(
        (a, b) => a.roundNumber - b.roundNumber
      );
      useKnockout = true;
    } else {
      // True legacy bracket: flat array of {pos, name, bye} entries
      return <LegacyBracket bracket={bracket} />;
    }
  }

  if (tournamentRounds.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-diagram-3 fs-1 text-muted"></i>
        <p className="text-muted mt-3">No bracket data available for this fixture</p>
      </div>
    );
  }

  return (
    <div className="container">
    <div className="tournament-bracket-container">
      <div className="bracket-scroll-hint">
        <i className="bi bi-arrow-left-right me-2"></i>
        Scroll horizontally to view all rounds
      </div>
      <div className="tournament-bracket">
        {tournamentRounds.map((round, roundIndex) => {
          const matchCount = round.matches.length;
          const maxMatches = Math.max(...tournamentRounds.map(r => r.matches.length), 1);
          // Fixed container height so space-around centers later rounds correctly
          const containerHeight = maxMatches * 100 + 16;

          return (
            <div
              key={roundIndex}
              className="bracket-round"
              data-round={roundIndex + 1}
            >
              <div className="round-header">
                <span className="round-name">{round.roundName}</span>
                <span className="round-matches">
                  {matchCount} {matchCount === 1 ? "match" : "matches"}
                </span>
              </div>
              <div
                className="round-matches-container"
                style={{ height: `${containerHeight}px` }}
              >
                {round.matches.map((match, mi) =>
                  useKnockout ? (
                    <KnockoutMatchCard key={mi} match={match} />
                  ) : isIndividualSport ? (
                    <IndividualMatchCard key={mi} match={match} />
                  ) : (
                    <TeamMatchCard key={mi} match={match} />
                  )
                )}
              </div>
            </div>
          );
        })}

        {/* Champion column after the Final */}
        {tournamentRounds.length > 0 && (
          <div className="bracket-round champion-round">
            <div className="round-header">
              <span className="round-name">Champion</span>
            </div>
            <div className="champion-card">
              {(() => {
                const finalRound =
                  tournamentRounds[tournamentRounds.length - 1];
                const finalMatch = finalRound?.matches[0];
                const winner = finalMatch?.winner;
                return winner?.name ? (
                  <>
                    <i className="bi bi-trophy-fill champion-trophy"></i>
                    <span className="champion-name">{winner.name}</span>
                  </>
                ) : (
                  <>
                    <i className="bi bi-trophy champion-trophy pending"></i>
                    <span className="champion-name pending">TBD</span>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

// ─── Legacy Bracket (flat pos/name/bye array) ────────────────────────────────
function LegacyBracket({ bracket }) {
  const bracketSize = Math.pow(
    2,
    Math.ceil(Math.log2(Math.max(bracket.length, 2)))
  );
  const totalRounds = Math.log2(bracketSize);
  const generatedRounds = [];
  let currentSize = bracketSize;
  let startPos = 0;

  for (let r = 0; r < totalRounds; r++) {
    const entries = bracket.filter(
      (b) => b.pos >= startPos && b.pos < startPos + currentSize
    );
    const roundName =
      r === totalRounds - 1
        ? "Final"
        : r === totalRounds - 2
          ? "Semi Final"
          : r === totalRounds - 3
            ? "Quarter Final"
            : `Round ${r + 1}`;
    generatedRounds.push({ name: roundName, entries });
    startPos += currentSize;
    currentSize = currentSize / 2;
  }

  return (
    <div className="tournament-bracket-container">
      <div className="tournament-bracket legacy">
        {generatedRounds.map((round, roundIndex) => (
          <div key={roundIndex} className="bracket-round">
            <div className="round-header">
              <span className="round-name">{round.name}</span>
            </div>
            <div className="round-matches-container">
              {round.entries.map((entry, idx) => (
                <div
                  key={idx}
                  className={`match-card legacy ${entry.bye ? "bye" : ""}`}
                >
                  <div className="match-teams">
                    <div className="team">
                      <span className="team-seed">{entry.pos + 1}</span>
                      <span className="team-name">
                        {entry.bye ? "BYE" : entry.name || "TBD"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Match Card Component with Logo Fetching ────────────────────────────────
function MatchCardWithLogos({ match, poolName }) {
  const [team1Logo, setTeam1Logo] = useState(null);
  const [team2Logo, setTeam2Logo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogos = async () => {
      try {
        // Get teamId from match data - fixture has teamId (string), not _id
        const team1Id = match.team1?.teamId || match.team1?._id;
        const team2Id = match.team2?.teamId || match.team2?._id;

        console.log(`🔍 MatchCardWithLogos Match ${match.matchNo}:`, {
          team1: { name: match.team1?.name, teamId: team1Id },
          team2: { name: match.team2?.name, teamId: team2Id }
        });

        // Get logo for team1
        let logo1 = null;
        if (team1Id) {
          logo1 = await fetchTeamLogo(team1Id);
        }
        setTeam1Logo(logo1 || "/img/कबड्डी.png");

        // Get logo for team2
        let logo2 = null;
        if (team2Id) {
          logo2 = await fetchTeamLogo(team2Id);
        }
        setTeam2Logo(logo2 || "/img/कबड्डी.png");
      } catch (error) {
        console.error("❌ Error fetching logos:", error);
        setTeam1Logo("/img/कबड्डी.png");
        setTeam2Logo("/img/कबड्डी.png");
      } finally {
        setLoading(false);
      }
    };

    fetchLogos();
  }, [match.team1?.teamId, match.team2?.teamId, match.team1?._id, match.team2?._id]);

  if (loading) {
    return (
      <div className="col-md-6">
        <div style={{ minHeight: "90px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Spinner animation="border" size="sm" variant="primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="col-md-6">
      <MatchScheduleCard
        matchNumber={`match-${match.matchNo}`}
        teamA={match.team1?.name || "TBD"}
        teamALogo={team1Logo}
        teamB={match.team2?.name || "TBD"}
        teamBLogo={team2Logo}
        time={match.scheduledTime || "TBA"}
        date={match.date || match.scheduledDate || match.scheduledAt || null}
      />
    </div>
  );
}

// ─── Pool Card Component with Inner Tabs ──────────────────────────────────
function PoolCard({ pool, matches, standings, qualifiersPerPool, data }) {
  const [activePoolTab, setActivePoolTab] = useState("standings");
  const totalExpected =
    ((pool.teams?.length || 0) * ((pool.teams?.length || 0) - 1)) / 2;

  return (
    <Col lg={12} xl={12}>
      <Card className="overflow-hidden" style={{
        background: "linear-gradient(135deg, #0d0028 0%, #1a0a3d 50%, #0d0028 100%)",
        border: "1px solid rgba(139, 92, 246, 0.3)",
        borderRadius: "16px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(139, 92, 246, 0.1)"
      }}>
        <Card.Header style={{
          background: "linear-gradient(90deg, #0a001f 0%, #1e0a4d 50%, #0a001f 100%)",
          borderBottom: "2px solid rgba(139, 92, 246, 0.4)",
          padding: "1rem 1.5rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <span style={{ fontWeight: "900", color: "#ffffff", fontSize: "1.4rem", letterSpacing: "3px", textTransform: "uppercase", textShadow: "0 0 20px rgba(139, 92, 246, 0.8)", fontFamily: "'Oswald', 'Montserrat', sans-serif" }}>
            <i className="bi bi-collection me-2" style={{ color: "#a78bfa" }}></i>
            POOL {pool.name}
          </span>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <span style={{ background: "rgba(255,255,255,0.1)", color: "#fff", padding: "0.25rem 0.75rem", borderRadius: "20px", fontSize: "0.8rem", fontWeight: "600", border: "1px solid rgba(255,255,255,0.2)", backdropFilter: "blur(10px)" }}>
              {pool.teams?.length || 0} teams
            </span>
            <span style={{ background: "rgba(139, 92, 246, 0.3)", color: "#d8b4fe", padding: "0.25rem 0.75rem", borderRadius: "20px", fontSize: "0.8rem", fontWeight: "600", border: "1px solid rgba(139, 92, 246, 0.5)", backdropFilter: "blur(10px)" }}>
              {matches.length || totalExpected} matches
            </span>
          </div>
        </Card.Header>

        {/* Inner Pool Tabs - Dark Esports Style */}
        <div style={{
          background: "rgba(255,255,255,0.03)",
          padding: "0.75rem",
          display: "flex",
          gap: "0.75rem",
          borderBottom: "1px solid rgba(139, 92, 246, 0.2)"
        }}>
          <button
            className="fw-bold d-flex align-items-center justify-content-center gap-2"
            onClick={() => setActivePoolTab("standings")}
            type="button"
            style={{
              flex: 1,
              padding: "0.8rem 1.5rem",
              background: activePoolTab === "standings"
                ? "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)"
                : "rgba(255,255,255,0.05)",
              color: "#ffffff",
              border: activePoolTab === "standings"
                ? "1px solid rgba(139, 92, 246, 0.8)"
                : "1px solid rgba(255,255,255,0.1)",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              fontSize: "0.9rem",
              fontWeight: "700",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              boxShadow: activePoolTab === "standings" ? "0 6px 24px rgba(124, 58, 237, 0.5)" : "none"
            }}
          >
            <i className="bi bi-bar-chart-fill" style={{ fontSize: "1.1rem" }}></i>
            <span>STANDINGS</span>
          </button>

          <button
            className="fw-bold d-flex align-items-center justify-content-center gap-2"
            onClick={() => setActivePoolTab("fixtures")}
            type="button"
            style={{
              flex: 1,
              padding: "0.8rem 1.5rem",
              background: activePoolTab === "fixtures"
                ? "linear-gradient(135deg, #059669 0%, #047857 100%)"
                : "rgba(255,255,255,0.05)",
              color: "#ffffff",
              border: activePoolTab === "fixtures"
                ? "1px solid rgba(5, 150, 105, 0.8)"
                : "1px solid rgba(255,255,255,0.1)",
              borderRadius: "10px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              fontSize: "0.9rem",
              fontWeight: "700",
              letterSpacing: "1.5px",
              textTransform: "uppercase",
              boxShadow: activePoolTab === "fixtures" ? "0 6px 24px rgba(5, 150, 105, 0.5)" : "none"
            }}
          >
            <i className="bi bi-calendar2-check-fill" style={{ fontSize: "1.1rem" }}></i>
            <span>FIXTURES</span>
          </button>
        </div>

        <Card.Body className="p-0" style={{ background: "transparent" }}>
          {/* Standings Tab Content - Esports Style */}
          {activePoolTab === "standings" && (
            <div style={{ padding: "1.5rem", animation: "fadeIn 0.3s ease", position: "relative" }}>

              {/* Ambient glow background */}
              <div style={{
                position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden",
                background: "radial-gradient(ellipse at 50% 0%, rgba(139, 92, 246, 0.12) 0%, transparent 65%)"
              }} />

              {/* Header Panel */}
              <div style={{
                background: "linear-gradient(90deg, rgba(20, 5, 60, 0.9) 0%, rgba(45, 15, 100, 0.9) 50%, rgba(20, 5, 60, 0.9) 100%)",
                backdropFilter: "blur(10px)",
                borderRadius: "12px",
                padding: "1.2rem 1.5rem",
                marginBottom: "1rem",
                border: "1px solid rgba(139, 92, 246, 0.35)",
                boxShadow: "0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: "2.4rem", fontWeight: "900", color: "#ffffff", letterSpacing: "4px", lineHeight: 1, textShadow: "0 2px 10px rgba(0,0,0,0.5)", fontFamily: "'Oswald', 'Montserrat', sans-serif" }}>POOL</div>
                    <div style={{ fontSize: "2.4rem", fontWeight: "900", color: "#a78bfa", letterSpacing: "4px", lineHeight: 1.1, textShadow: "0 0 24px rgba(167,139,250,0.7)", fontFamily: "'Oswald', 'Montserrat', sans-serif" }}>STANDINGS</div>
                  </div>
                  <i className="bi bi-bar-chart-fill" style={{ fontSize: "3.5rem", color: "rgba(167, 139, 250, 0.25)" }} />
                </div>
              </div>

              {/* Column Headers */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "48px 48px 1fr 44px 44px 44px 44px 60px 60px",
                padding: "0.4rem 1rem",
                marginBottom: "0.4rem",
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.85rem",
                fontWeight: "700",
                letterSpacing: "2px",
                textTransform: "uppercase",
                fontFamily: "'Montserrat', sans-serif"
              }}>
                <div>#</div>
                <div></div>
                <div>TEAM</div>
                <div style={{ textAlign: "center" }}>P</div>
                <div style={{ textAlign: "center" }}>W</div>
                <div style={{ textAlign: "center" }}>D</div>
                <div style={{ textAlign: "center" }}>L</div>
                <div style={{ textAlign: "center" }}>GD</div>
                <div style={{ textAlign: "center" }}>PTS</div>
              </div>

              {/* Team Rows */}
              <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                {(standings.length > 0
                  ? standings
                  : (pool.teams || []).map(t => ({ teamId: t.teamId || t._id, name: t.name, played: 0, won: 0, drawn: 0, lost: 0, goalDifference: 0, points: 0 }))
                ).map((s, sIdx) => {
                  const isQualifier = sIdx < qualifiersPerPool && data.format === "pools-and-knockout";
                  const rankColors = ["#ffd700", "#c0c0c0", "#cd7f32"];
                  const rankColor = rankColors[sIdx] || "rgba(255,255,255,0.4)";
                  return (
                    <div key={s.teamId || sIdx} style={{
                      display: "grid",
                      gridTemplateColumns: "48px 48px 1fr 44px 44px 44px 44px 60px 60px",
                      alignItems: "center",
                      gap: "0.25rem",
                      background: isQualifier
                        ? "linear-gradient(90deg, rgba(109,40,217,0.45) 0%, rgba(91,33,182,0.35) 60%, rgba(76,29,149,0.3) 100%)"
                        : "linear-gradient(90deg, rgba(130,20,20,0.88) 0%, rgba(100,12,12,0.82) 55%, rgba(70,8,8,0.78) 100%)",
                      borderRadius: "10px",
                      padding: "0.85rem 1rem",
                      border: isQualifier
                        ? "1px solid rgba(139,92,246,0.55)"
                        : "1px solid rgba(255,255,255,0.05)",
                      boxShadow: isQualifier
                        ? "0 4px 20px rgba(139,92,246,0.25), inset 0 1px 0 rgba(255,255,255,0.08)"
                        : "0 2px 12px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)"
                    }}>
                      {/* Rank Badge */}
                      <div style={{
                        width: "42px", height: "42px",
                        background: sIdx < 3 ? `radial-gradient(circle, ${rankColor}22, ${rankColor}08)` : "rgba(255,255,255,0.07)",
                        border: sIdx < 3 ? `2px solid ${rankColor}` : "2px solid rgba(255,255,255,0.15)",
                        borderRadius: "50%",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "1.1rem", fontWeight: "900",
                        color: sIdx < 3 ? rankColor : "rgba(255,255,255,0.55)",
                        textShadow: sIdx < 3 ? `0 0 12px ${rankColor}` : "none",
                        flexShrink: 0,
                        fontFamily: "'Montserrat', sans-serif"
                      }}>
                        {sIdx + 1}
                      </div>

                      {/* Team Logo */}
                      <StandingsTeamLogo teamId={s.teamId || s._id} />

                      {/* Team Name — broadcast typography */}
                      <div style={{
                        color: "#ffffff",
                        fontFamily: "'Oswald', 'Montserrat', 'Arial Black', sans-serif",
                        fontWeight: "700",
                        fontSize: "1.3rem",
                        letterSpacing: "2px",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textShadow: "0 0 14px rgba(255,255,255,0.25), 0 2px 6px rgba(0,0,0,0.7)",
                        paddingLeft: "0.25rem"
                      }}>
                        {s.name}
                      </div>

                      {/* P */}
                      <div style={{ textAlign: "center", color: "rgba(255,255,255,0.6)", fontWeight: "700", fontSize: "1.1rem", fontFamily: "'Montserrat', sans-serif" }}>{s.played ?? 0}</div>
                      {/* W */}
                      <div style={{ textAlign: "center", color: "#4ade80", fontWeight: "700", fontSize: "1.1rem", fontFamily: "'Montserrat', sans-serif" }}>{s.won ?? 0}</div>
                      {/* D */}
                      <div style={{ textAlign: "center", color: "#facc15", fontWeight: "700", fontSize: "1.1rem", fontFamily: "'Montserrat', sans-serif" }}>{s.drawn ?? 0}</div>
                      {/* L */}
                      <div style={{ textAlign: "center", color: "#f87171", fontWeight: "700", fontSize: "1.1rem", fontFamily: "'Montserrat', sans-serif" }}>{s.lost ?? 0}</div>
                      {/* GD */}
                      <div style={{ textAlign: "center", fontWeight: "700", fontSize: "1.1rem", fontFamily: "'Montserrat', sans-serif", color: (s.goalDifference ?? 0) >= 0 ? "#4ade80" : "#f87171" }}>
                        {(s.goalDifference ?? 0) >= 0 ? "+" : ""}{s.goalDifference ?? 0}
                      </div>
                      {/* PTS */}
                      <div style={{ textAlign: "center", fontWeight: "900", fontSize: "1.4rem", fontFamily: "'Oswald', 'Montserrat', sans-serif", color: "#ffffff", textShadow: "0 0 16px rgba(255,255,255,0.5)", letterSpacing: "1px" }}>
                        {s.points ?? 0}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Fixtures Tab Content */}
          {activePoolTab === "fixtures" && (
            <div style={{ padding: "1.5rem", animation: "fadeIn 0.3s ease" }}>
              {/* Fixtures Header Panel */}
              <div style={{
                background: "linear-gradient(90deg, rgba(5,40,25,0.9) 0%, rgba(10,70,40,0.9) 50%, rgba(5,40,25,0.9) 100%)",
                backdropFilter: "blur(10px)",
                borderRadius: "12px",
                padding: "1.2rem 1.5rem",
                marginBottom: "1.5rem",
                border: "1px solid rgba(5,150,105,0.35)",
                boxShadow: "0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
                display: "flex",
                alignItems: "center",
                gap: "1rem"
              }}>
                <i className="bi bi-calendar2-check-fill" style={{ fontSize: "2rem", color: "#34d399" }} />
                <div>
                  <div style={{ fontSize: "1.3rem", fontWeight: "900", color: "#ffffff", letterSpacing: "2px", textTransform: "uppercase" }}>ROUND ROBIN FIXTURES</div>
                  <div style={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.45)", fontWeight: "600", letterSpacing: "1px" }}>Match Schedule & Results</div>
                </div>
              </div>
              
              <div className="row g-3">
                {matches.length > 0
                  ? matches.map((match, mi) => (
                      <MatchCardWithLogos 
                        key={match.matchNo ?? mi} 
                        match={match}
                        poolName={pool.name}
                      />
                    ))
                  : /* Preview rows when no actual matches yet */
                    (() => {
                      const teams = pool.teams || [];
                      const preview = [];
                      let matchNum = 1;
                      for (let i = 0; i < teams.length; i++) {
                        for (let j = i + 1; j < teams.length; j++) {
                          preview.push({
                            matchNo: matchNum,
                            team1: teams[i],
                            team2: teams[j],
                          });
                          matchNum++;
                        }
                      }
                      return preview.map((m) => (
                        <MatchCardWithLogos
                          key={m.matchNo}
                          match={m}
                          poolName={pool.name}
                        />
                      ));
                    })()}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
}

// ─── Pools Tab ───────────────────────────────────────────────────────────────
function PoolsTab({ data }) {
  const pools = data.pools || [];
  const poolMatches = data.poolMatches || [];
  const poolStandings = data.poolStandings || {};

  if (pools.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-people fs-1 text-muted"></i>
        <p className="text-muted mt-3">No pools defined for this fixture</p>
      </div>
    );
  }

  const qualifiersPerPool =
    data.format === "pools-and-knockout" ? 2 : pools[0]?.teams?.length || 0;

  // Normalize pool name: match both "A" and "Pool A" variants
  const normalizePoolName = (n) => (n || "").replace(/^Pool\s+/i, "").trim();
  const getPoolMatches = (poolName) =>
    poolMatches.filter(
      (m) =>
        m.poolName === poolName ||
        m.poolName === `Pool ${poolName}` ||
        normalizePoolName(m.poolName) === normalizePoolName(poolName)
    );

  const getStandings = (poolName) =>
    poolStandings[poolName] ||
    poolStandings[`Pool ${poolName}`] ||
    [];

  return (
    <div className="container py-3">
      {/* Points system info */}
      {data.configuration && (
        <div className="alert alert-info mb-4" style={{ borderLeft: "4px solid #0d6efd" }}>
          <div className="d-flex align-items-center gap-3">
            <i className="bi bi-info-circle fs-4"></i>
            <div>
              <strong>Points System:</strong> Win:{" "}
              {data.configuration.pointsForWin ?? 3} pts | Draw:{" "}
              {data.configuration.pointsForDraw ?? 1} pts | Loss:{" "}
              {data.configuration.pointsForLoss ?? 0} pts
            </div>
          </div>
        </div>
      )}

      <Row className="g-4">
        {pools.map((pool, idx) => {
          const matches = getPoolMatches(pool.name);
          const standings = getStandings(pool.name);

          return (
            <PoolCard
              key={idx}
              pool={pool}
              matches={matches}
              standings={standings}
              qualifiersPerPool={qualifiersPerPool}
              data={data}
            />
          );
        })}
      </Row>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

// ─── Heats Tab ───────────────────────────────────────────────────────────────
function HeatsTab({ heats }) {
  if (!heats || heats.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-speedometer2 fs-1 text-muted"></i>
        <p className="text-muted mt-3">No heats defined for this fixture</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <Row className="g-4">
        {heats.map((heat, heatIdx) => (
          <Col key={heatIdx} lg={6} xl={4}>
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
                <span className="fw-bold">
                  <i className="bi bi-flag me-2"></i>Heat {heat.heatNumber}
                </span>
                <Badge bg="dark">
                  {heat.lanes.filter((l) => l.athlete).length} athletes
                </Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "60px" }}>Lane</th>
                      <th>Athlete</th>
                      <th style={{ width: "80px" }}>Bib</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heat.lanes.map((lane, laneIdx) => (
                      <tr
                        key={laneIdx}
                        className={!lane.athlete ? "table-secondary" : ""}
                      >
                        <td className="text-center fw-bold">{lane.laneNumber}</td>
                        <td>
                          {lane.athlete ? (
                            <div>
                              <i className="bi bi-person-fill me-2 text-primary"></i>
                              {lane.athlete.name}
                              {lane.athlete.district && (
                                <div className="small text-muted">
                                  {lane.athlete.district}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted fst-italic">
                              Empty Lane
                            </span>
                          )}
                        </td>
                        <td>
                          {lane.athlete?.bibNumber && (
                            <Badge bg="info">{lane.athlete.bibNumber}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}

// ─── Overview Tab ────────────────────────────────────────────────────────────
function OverviewTab({ data }) {
  const pools = data.pools || [];
  const rounds = data.rounds || [];
  const individualRounds = data.individualRounds || [];
  const poolMatches = data.poolMatches || [];
  const knockoutMatches = data.knockoutMatches || [];

  const isIndividualSport =
    data.type === "individual" || individualRounds.length > 0;
  const isPoolsAndKnockout = data.format === "pools-and-knockout";

  const getEventName = () =>
    typeof data.eventId === "object" && data.eventId !== null
      ? data.eventId.name || "Unknown Event"
      : "Unknown Event";

  const getSportName = () => {
    if (data.sportId && typeof data.sportId === "object") {
      return data.sportId.name || data.sport || "Unknown Sport";
    }
    return data.sport || "Unknown Sport";
  };

  // Calculate stats
  let totalMatches = 0;
  let completedMatches = 0;
  let totalRounds = data.totalRounds || 0;
  let totalTeams = data.totalTeams || 0;

  const expectedPoolMatches = pools.reduce((sum, pool) => {
    const n = pool.teams?.length || 0;
    return sum + (n * (n - 1)) / 2;
  }, 0);

  if (isPoolsAndKnockout) {
    const poolActual = poolMatches.length || expectedPoolMatches;
    const poolCompleted = poolMatches.filter((m) => m.status === "completed").length;

    // Resolve knockout source — same priority as the bracket renderer:
    // 1. legacy rounds  2. knockoutRounds  3. knockoutMatches  4. bracket-as-KnockoutMatch
    const knockoutRounds = data.knockoutRounds || [];
    const bracketAsKnockout = (data.bracket || []).filter(
      (m) => m.roundNumber !== undefined || m.matchNo !== undefined
    );
    const knockoutRoundMatches = isIndividualSport
      ? individualRounds.reduce((s, r) => s + r.matches.length, 0)
      : rounds.reduce((s, r) => s + r.matches.length, 0);

    let knockoutSource =
      knockoutRoundMatches > 0
        ? (isIndividualSport ? individualRounds : rounds).flatMap((r) => r.matches)
        : knockoutRounds.length > 0
        ? knockoutRounds.flatMap((r) => r.matches)
        : knockoutMatches.length > 0
        ? knockoutMatches
        : bracketAsKnockout;

    let knockoutActual = 0;
    let knockoutCompleted = 0;

    if (knockoutSource.length > 0) {
      const realKO = knockoutSource.filter((m) => !m.isBye);
      knockoutActual = realKO.length;
      knockoutCompleted = realKO.filter((m) => m.status === "completed").length;
    } else if (data.poolStageComplete) {
      // Bracket not yet populated — estimate
      const qualifiers = data.totalQualifiers || pools.length * 2;
      knockoutActual = qualifiers > 0 ? qualifiers - 1 : 0;
    }

    totalMatches = poolActual + knockoutActual;
    completedMatches = poolCompleted + knockoutCompleted;
    totalRounds += 1; // +1 for pool stage
    if (!totalTeams && pools.length > 0) {
      totalTeams = pools.reduce((s, p) => s + (p.teams?.length || 0), 0);
    }
  } else {
    const roundTotal = isIndividualSport
      ? individualRounds.reduce((s, r) => s + r.matches.length, 0)
      : rounds.reduce((s, r) => s + r.matches.length, 0);
    totalMatches = roundTotal || poolMatches.length || expectedPoolMatches;
    completedMatches =
      roundTotal > 0
        ? isIndividualSport
          ? individualRounds.reduce(
              (s, r) => s + r.matches.filter((m) => m.status === "completed").length,
              0
            )
          : rounds.reduce(
              (s, r) => s + r.matches.filter((m) => m.status === "completed").length,
              0
            )
        : poolMatches.filter((m) => m.status === "completed").length;

    if (!totalTeams && pools.length > 0) {
      totalTeams = pools.reduce((s, p) => s + (p.teams?.length || 0), 0);
    }
  }

  const cardBase = {
    background: "linear-gradient(135deg, #0d0028 0%, #1a0a3d 50%, #0d0028 100%)",
    border: "1px solid rgba(139, 92, 246, 0.3)",
    borderRadius: "16px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(139, 92, 246, 0.1)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  };

  const cardHeader = {
    background: "linear-gradient(90deg, #0a001f 0%, #1e0a4d 50%, #0a001f 100%)",
    borderBottom: "2px solid rgba(139, 92, 246, 0.4)",
    padding: "1rem 1.5rem",
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  };

  const headerText = {
    fontWeight: "900",
    color: "#ffffff",
    fontSize: "1.2rem",
    letterSpacing: "3px",
    textTransform: "uppercase",
    textShadow: "0 0 20px rgba(139, 92, 246, 0.8)",
    fontFamily: "'Oswald', 'Montserrat', sans-serif",
  };

  const ambientGlow = {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background: "radial-gradient(ellipse at 50% 0%, rgba(139, 92, 246, 0.12) 0%, transparent 65%)",
  };

  const infoRows = [
    { label: "EVENT", value: getEventName(), color: "#ffffff", borderColor: "rgba(255,255,255,0.2)", bg: "rgba(255,255,255,0.08)", plain: true },
    { label: "SPORT", value: getSportName(), color: "#ffffff", borderColor: "rgba(255,255,255,0.2)", bg: "rgba(255,255,255,0.1)" },
    { label: "TYPE", value: data.type, color: "#6ea8fe", borderColor: "rgba(13,110,253,0.5)", bg: "rgba(13,110,253,0.2)" },
    { label: "FORMAT", value: data.format, color: "#adb5bd", borderColor: "rgba(108,117,125,0.4)", bg: "rgba(108,117,125,0.2)" },
    { label: "STATUS", value: data.locked ? "Locked" : "Open", color: data.locked ? "#ea868f" : "#75b798", borderColor: data.locked ? "rgba(220,53,69,0.5)" : "rgba(25,135,84,0.5)", bg: data.locked ? "rgba(220,53,69,0.2)" : "rgba(25,135,84,0.2)" },
  ];

  const statItems = [
    { value: totalTeams, label: isIndividualSport ? "ATHLETES" : "TEAMS", color: "#60a5fa", glow: "rgba(96,165,250,0.4)" },
    { value: totalRounds, label: data.format === "pools-only" ? "STAGE" : "ROUNDS", color: "#a78bfa", glow: "rgba(167,139,250,0.4)" },
    { value: totalMatches, label: "TOTAL MATCHES", color: "#fbbf24", glow: "rgba(251,191,36,0.4)" },
    { value: completedMatches, label: "COMPLETED", color: "#34d399", glow: "rgba(52,211,153,0.4)" },
  ];

  return (
    <div className="container py-3">
      <Row className="g-4">
        {/* Tournament Info */}
        <Col lg={6} className="d-flex">
          <div style={{ ...cardBase, width: "100%" }}>
            <div style={cardHeader}>
              <i className="bi bi-info-circle-fill" style={{ color: "#a78bfa", fontSize: "1.3rem" }}></i>
              <span style={headerText}>TOURNAMENT INFO</span>
            </div>
            <div style={{ padding: "1.5rem", position: "relative", flex: 1 }}>
              <div style={ambientGlow} />
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", position: "relative" }}>
                {infoRows.map(({ label, value, color, borderColor, bg, plain }) => (
                  <div key={label} style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0.65rem 1rem",
                    background: "rgba(255,255,255,0.04)",
                    borderRadius: "10px",
                    border: "1px solid rgba(139, 92, 246, 0.15)",
                  }}>
                    <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.78rem", fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase" }}>
                      {label}
                    </span>
                    {plain ? (
                      <strong style={{ color: "#ffffff", fontSize: "0.9rem", maxWidth: "60%", textAlign: "right" }}>{value}</strong>
                    ) : (
                      <span style={{ background: bg, color, padding: "0.28rem 0.8rem", borderRadius: "20px", fontSize: "0.78rem", fontWeight: "700", border: `1px solid ${borderColor}`, letterSpacing: "1px" }}>
                        {value}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>

        {/* Statistics */}
        <Col lg={6} className="d-flex">
          <div style={{ ...cardBase, width: "100%" }}>
            <div style={cardHeader}>
              <i className="bi bi-graph-up-arrow" style={{ color: "#a78bfa", fontSize: "1.3rem" }}></i>
              <span style={headerText}>STATISTICS</span>
            </div>
            <div style={{ padding: "1.5rem", position: "relative", flex: 1, display: "flex", alignItems: "center" }}>
              <div style={ambientGlow} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", position: "relative", width: "100%" }}>
                {statItems.map(({ value, label, color, glow }) => (
                  <div key={label} style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(139, 92, 246, 0.15)",
                    borderRadius: "12px",
                    padding: "1.5rem 1rem",
                    textAlign: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      position: "absolute", inset: 0, pointerEvents: "none",
                      background: `radial-gradient(ellipse at 50% 0%, ${glow.replace("0.4)", "0.1)")} 0%, transparent 70%)`,
                    }} />
                    <div style={{ fontSize: "2.8rem", fontWeight: "900", color, lineHeight: 1, textShadow: `0 0 20px ${glow}`, fontFamily: "'Oswald', 'Montserrat', sans-serif", position: "relative" }}>
                      {value}
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", fontWeight: "700", letterSpacing: "2px", textTransform: "uppercase", marginTop: "0.5rem", position: "relative" }}>
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}

// ─── Main Page Component ─────────────────────────────────────────────────────
export default function MatchFixturePage() {
  const [fixture, setFixture] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("pools"); // default to pools since data is there

  useEffect(() => {
    setLoading(true);

    const loadFixture = (url) =>
      fetch(url, { method: "GET", credentials: "include" }).then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      });

    loadFixture(FIXTURE_API_URL)
      .catch(() => loadFixture(FIXTURE_PUBLIC_URL))
      .then((json) => {
        console.log("[MatchFixturePage] raw json:", json);
        if (!json.success) throw new Error(json.message || "API error");

        // Safe extraction: handles both json.data = fixture AND json.data.data = fixture
        const raw = json.data;
        const fixtureData = raw?._id ? raw : (raw?.data ?? raw);

        if (!fixtureData) throw new Error("No fixture data returned");
        console.log("[MatchFixturePage] type:", fixtureData.type, "format:", fixtureData.format);
        console.log("[MatchFixturePage] pools:", fixtureData.pools?.length,
          "knockoutMatches:", fixtureData.knockoutMatches?.length,
          "bracket:", fixtureData.bracket?.length,
          "poolMatches:", fixtureData.poolMatches?.length);
        if (fixtureData.pools?.length) console.log("[MatchFixturePage] pool[0].name:", fixtureData.pools[0]?.name);
        if (fixtureData.poolMatches?.length) console.log("[MatchFixturePage] poolMatches[0].poolName:", fixtureData.poolMatches[0]?.poolName);

        setFixture(fixtureData);
      })
      .catch((err) => {
        console.error("[MatchFixturePage] error:", err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  // Build tab list from fixture data
  const getTabs = (data) => {
    if (!data) return [];
    const tabs = [];
    const hasKnockout =
      (data.rounds?.length > 0) ||
      (data.individualRounds?.length > 0) ||
      (data.bracket?.length > 0) ||
      (data.knockoutMatches?.length > 0) ||
      (data.knockoutRounds?.length > 0) ||
      data.type === "knockout" || data.type === "pool_knockout" ||
      data.format === "knockout-only" || data.format === "pools-and-knockout" ||
      data.competitionFormat === "KNOCKOUT" || data.competitionFormat === "GROUP_STAGE_KNOCKOUT";

    if (hasKnockout) tabs.push({ key: "bracket", label: "Tournament Bracket", icon: "bi-trophy" });
    if (data.pools?.length > 0) tabs.push({ key: "pools", label: "Pools", icon: "bi-grid-3x3-gap-fill" });
    if (data.heats?.length > 0) tabs.push({ key: "heats", label: "Heats", icon: "bi-lightning-charge-fill" });
    tabs.push({ key: "overview", label: "Overview", icon: "bi-bar-chart-fill" });
    return tabs;
  };

  const tabs = getTabs(fixture);
  const currentTab = tabs.length > 0
    ? (tabs.find((t) => t.key === activeTab) ? activeTab : tabs[0].key)
    : activeTab;

  const renderTabContent = () => {
    if (!fixture) return null;
    switch (currentTab) {
      case "bracket": return <TabErrorBoundary><BracketTab data={fixture} /></TabErrorBoundary>;
      case "pools":   return <TabErrorBoundary><PoolsTab data={fixture} /></TabErrorBoundary>;
      case "heats":   return <TabErrorBoundary><HeatsTab heats={fixture.heats || []} /></TabErrorBoundary>;
      case "overview": return <TabErrorBoundary><OverviewTab data={fixture} /></TabErrorBoundary>;
      default:         return <TabErrorBoundary><OverviewTab data={fixture} /></TabErrorBoundary>;
    }
  };

  return (
    <>
      <Breadcrumb
        pageName="MatchFixturePage"
        pageTitle="Match Fixture"
        img_url="/img/BWKLE2-Desktop.jpeg"
        mobile_banner="/img/BWKLE2-Mobile.jpeg"
      />

      <div>
        <h1 className={`${stylesgfi.title}`}>
          द्वितीय बिहार महिला कबड्डी लीग 2026 — मैच फिक्सचर
        </h1>
      </div>

      <div className="container-fluid pb-5">
        {/* Loading */}
        {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading fixture data...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="alert alert-danger m-4">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Failed to load fixture data:</strong> {error}
            <div className="mt-2 small text-muted">
              Backend must be running on port 5012. Check console (F12) for details.
            </div>
          </div>
        )}

        {/* Main content */}
        {!loading && fixture && (
          <div style={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(139,92,246,0.1)", border: "1px solid rgba(139,92,246,0.3)" }}>

            {/* ── Tab nav card ── */}
            <div style={{
              background: "linear-gradient(90deg, #060014 0%, #130030 50%, #060014 100%)",
              borderBottom: "1px solid rgba(139,92,246,0.25)",
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}>
              {tabs.map((tab) => {
                const isActive = currentTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 22px",
                      border: isActive
                        ? "1px solid rgba(139,92,246,0.7)"
                        : "1px solid rgba(255,255,255,0.08)",
                      borderRadius: "0",
                      cursor: "pointer",
                      transition: "all 0.25s ease",
                      fontFamily: "'Oswald','Montserrat',sans-serif",
                      fontWeight: "700",
                      fontSize: "0.78rem",
                      letterSpacing: "2px",
                      textTransform: "uppercase",
                      position: "relative",
                      overflow: "hidden",
                      background: isActive
                        ? "linear-gradient(135deg, #3b0d8a 0%, #5b21b6 60%, #3b0d8a 100%)"
                        : "rgba(255,255,255,0.04)",
                      color: isActive ? "#ffffff" : "rgba(196,181,253,0.6)",
                      boxShadow: isActive
                        ? "0 4px 20px rgba(124,58,237,0.45), 0 0 30px rgba(124,58,237,0.15), inset 0 1px 0 rgba(255,255,255,0.1)"
                        : "none",
                      clipPath: "polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)",
                    }}
                  >
                    {/* top shimmer on active */}
                    {isActive && (
                      <span style={{
                        position: "absolute", top: 0, left: 0, right: 0,
                        height: "2px",
                        background: "linear-gradient(90deg, transparent, #a78bfa, #7c3aed, #a78bfa, transparent)",
                      }} />
                    )}
                    <i className={`bi ${tab.icon}`} style={{
                      fontSize: "0.95rem",
                      color: isActive ? "#c4b5fd" : "rgba(167,139,250,0.5)",
                      filter: isActive ? "drop-shadow(0 0 6px rgba(167,139,250,0.8))" : "none",
                    }} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Tab content */}
            <div style={{
              minHeight: "500px",
              background: "linear-gradient(135deg, #0d0028 0%, #1a0a3d 50%, #0d0028 100%)",
            }}>
              {renderTabContent()}
            </div>

            {/* Footer */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              padding: "12px 20px",
              background: "linear-gradient(90deg, #060014 0%, #130030 50%, #060014 100%)",
              borderTop: "1px solid rgba(139,92,246,0.2)",
            }}>
              <button
                onClick={() => window.print()}
                style={{
                  display: "flex", alignItems: "center", gap: "7px",
                  padding: "7px 18px",
                  background: "rgba(139,92,246,0.15)",
                  border: "1px solid rgba(139,92,246,0.35)",
                  borderRadius: "0",
                  color: "#c4b5fd",
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  fontFamily: "'Oswald','Montserrat',sans-serif",
                  clipPath: "polygon(6px 0%, 100% 0%, calc(100% - 6px) 100%, 0% 100%)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = "rgba(139,92,246,0.3)";
                  e.currentTarget.style.color = "#ffffff";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = "rgba(139,92,246,0.15)";
                  e.currentTarget.style.color = "#c4b5fd";
                }}
              >
                <i className="bi bi-printer-fill" style={{ fontSize: "0.85rem" }} />
                Print
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
